import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ShieldBan, ShieldCheck, Link2 } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import type { UserStatus } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs } from '@/components/ui/Tabs';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { useSession } from '@/features/session/useSession';
import { useBackHref } from '@/hooks/useListLocation';
import { PasskeyRegistrationModal } from './PasskeyRegistrationModal';
import { hasPermission } from '@/lib/permissions';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import {
  userDisplayName,
  userStatusLabel,
  userStatusTone,
  userTypeLabel,
} from '@/lib/labels';
import {
  BlockUserDoc,
  UnblockUserDoc,
  IssueMagicLinkDoc,
  UserDoc,
  UserLoyaltyDoc,
  UserRolesCatalogDoc,
} from './userDocuments';
import { useUserForm, type UserForm, USER_TYPE_FIELD_DESCRIPTION, USER_TYPE_OPTIONS } from './useUserForm';
import { UserValuePanel } from './UserValuePanel';
import { UserInformationTab } from './UserInformationTab';
import { UserAccessTab, type PasskeyLink } from './UserAccessTab';
import { UserLoyaltyTab } from './UserLoyaltyTab';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { UserSaveFooter } from './UserSaveFooter';

const USER_TABS = [
  { id: 'information', label: 'Information' },
  { id: 'access', label: 'Access' },
  { id: 'loyalty', label: 'Loyalty' },
] as const;

/**
 * One user record across three tabs (Information / Access / Loyalty), with the
 * `Loyalty & value` stats pinned above the tabs so they stay on screen.
 *
 * Information and Access share one form state, so either Save writes the whole
 * record. Creating a user is a single short form instead — there is nothing to
 * tab through until the account exists.
 */
export function UserEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The users list as it was left (search, filters, sort, page): Cancel returns to it, and a newly created user's page (opened with the same `state`) does too.
  const backHref = useBackHref('/users');
  const isNew = !id || id === 'new';
  const { me } = useSession();
  const [params, setParams] = useSearchParams();

  const { data, loading, error, refetch } = useQuery(UserDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
    errorPolicy: 'all',
  });
  const { data: rolesCatalog } = useQuery(UserRolesCatalogDoc);
  const user = data?.user ?? null;

  const loyaltyQuery = useQuery(UserLoyaltyDoc, {
    variables: { userId: user?.id ?? '' },
    skip: !user,
    errorPolicy: 'all',
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [passkeyLink, setPasskeyLink] = useState<PasskeyLink | null>(null);
  const [magicLink, setMagicLink] = useState<PasskeyLink | null>(null);

  const form = useUserForm({
    user,
    id,
    isNew,
    onCreated: (newId) => navigate(`/users/${newId}`, { replace: true, state: location.state }),
    onSaved: refetch,
  });

  const [blockUser, { loading: blocking }] = useMutation(BlockUserDoc);
  const [unblockUser, { loading: unblocking }] = useMutation(UnblockUserDoc);
  const [issueMagicLink, { loading: issuingMagicLink }] = useMutation(IssueMagicLinkDoc);

  const scopeOptions = useMemo(
    () =>
      (me?.allowedScopes ?? []).map((scope) => ({
        value: scope.id,
        label: scope.name,
      })),
    [me?.allowedScopes],
  );

  const handleBlockToggle = async () => {
    if (!id || !user) return;
    setFormError(null);
    try {
      if (user.status === 'BLOCKED') {
        await unblockUser({ variables: { id } });
      } else {
        if (
          !window.confirm(
            'Block this account? They will not be able to sign in.',
          )
        )
          return;
        await blockUser({ variables: { id } });
      }
      await refetch();
    } catch (err) {
      setFormError(graphqlErrorMessage(err));
    }
  };

  const canBlock =
    hasPermission(me?.permissions, 'user:write') &&
    user &&
    (user.status === 'ACTIVE' ||
      user.status === 'CREATED' ||
      user.status === 'BLOCKED');

  const canIssueMagicLink =
    hasPermission(me?.permissions, 'user:issue_magic_link') &&
    user &&
    (user.type == null || user.type === 'EMPLOYEE' || user.type === 'CONTRACTOR') &&
    (user.status === 'ACTIVE' ||
      user.status === 'CREATED' ||
      user.status === 'IMPORTED');

  const loyalty =
    loyaltyQuery.data?.userLoyalty ?? loyaltyQuery.previousData?.userLoyalty;

  const active =
    USER_TABS.find((t) => t.id === params.get('tab'))?.id ?? 'information';

  const items = useMemo(() => {
    if (!user) return [];
    return USER_TABS.map((tab) => ({
      id: tab.id,
      label: tab.label,
      content:
        tab.id === 'information' ? (
          <UserInformationTab
            user={user}
            form={form}
            onCancel={() => navigate(backHref)}
          />
        ) : tab.id === 'access' ? (
          <UserAccessTab
            user={user}
            form={form}
            scopeOptions={scopeOptions}
            rolesCatalog={rolesCatalog?.roles ?? []}
            onCancel={() => navigate(backHref)}
            onRefetch={refetch}
            onPasskeyLink={setPasskeyLink}
          />
        ) : (
          <UserLoyaltyTab
            userId={user.id}
            email={user.email}
            loyalty={loyalty}
            loading={loyaltyQuery.loading}
            error={loyaltyQuery.error}
            goldenTicket={user.goldenTicket ?? null}
            onRefetchLoyalty={loyaltyQuery.refetch}
            onGoldenChange={refetch}
          />
        ),
    }));
  }, [
    user,
    form,
    scopeOptions,
    rolesCatalog?.roles,
    loyalty,
    loyaltyQuery.loading,
    loyaltyQuery.error,
    loyaltyQuery.refetch,
    refetch,
    navigate,
    backHref,
  ]);

  if (!isNew && loading && !user) {
    return <p className="text-sm text-gray-500">Loading user…</p>;
  }

  if (!isNew && error && !user) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          User
        </h1>
        <QueryErrorNotice error={error} title="Could not load this user" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {isNew ? 'New user' : `Edit ${user ? userDisplayName(user) : ''}`}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isNew
              ? 'After creating the account, generate a passkey registration link to let staff sign in.'
              : 'Status is not free-form: use block / unblock. Imported and deleted are set by import and self-delete only.'}
          </p>
        </div>
        {user ? (
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={userStatusTone(user.status as UserStatus)}>
              {userStatusLabel(user.status as UserStatus)}
            </Badge>
            <Badge tone="neutral">{userTypeLabel(user.type)}</Badge>
            {canIssueMagicLink ? (
              <Button
                id="admin.users.issueMagicLink"
                type="button"
                variant="secondary"
                size="sm"
                loading={issuingMagicLink}
                leftIcon={<Link2 className="h-4 w-4" />}
                onClick={async () => {
                  if (!id) return;
                  setFormError(null);
                  try {
                    const result = await issueMagicLink({ variables: { userId: id } });
                    const link = result.data?.issueMagicLink;
                    if (link) setMagicLink(link);
                  } catch (err) {
                    setFormError(graphqlErrorMessage(err));
                  }
                }}
              >
                Generate magic link
              </Button>
            ) : null}
            {canBlock ? (
              <Button
                type="button"
                variant={user.status === 'BLOCKED' ? 'secondary' : 'danger'}
                size="sm"
                loading={blocking || unblocking}
                leftIcon={
                  user.status === 'BLOCKED' ? (
                    <ShieldCheck className="h-4 w-4" />
                  ) : (
                    <ShieldBan className="h-4 w-4" />
                  )
                }
                onClick={() => void handleBlockToggle()}
              >
                {user.status === 'BLOCKED' ? 'Unblock' : 'Block'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {(formError ?? form.error) ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {formError ?? form.error}
        </p>
      ) : null}

      {isNew ? (
        <UserCreateForm
          form={form}
          scopeOptions={scopeOptions}
          onCancel={() => navigate(backHref)}
        />
      ) : user ? (
        <>
          <UserValuePanel
            pointsBalance={loyalty?.pointsBalance ?? user.pointsBalance}
            visitCount={loyalty?.visitCount ?? user.visitCount}
            spendCents={loyalty?.spendCents ?? user.spendCents}
            tipCents={loyalty?.tipCents ?? user.tipCents}
            lastVisitAt={loyalty?.lastVisitAt ?? user.lastVisitAt}
          />
          <Tabs
            items={items}
            value={active}
            onChange={(tabId) => {
              const next = new URLSearchParams(params);
              next.set('tab', tabId);
              // Keep `location.state` (`from`): Cancel must still return to the filtered list after a tab change.
              setParams(next, { replace: true, state: location.state });
            }}
          />
        </>
      ) : null}

      <PasskeyRegistrationModal
        open={passkeyLink !== null}
        link={passkeyLink}
        onClose={() => setPasskeyLink(null)}
      />
      <PasskeyRegistrationModal
        open={magicLink !== null}
        link={magicLink}
        title="Magic link"
        description={
          magicLink
            ? `Same login URL as the email. Valid until ${new Date(magicLink.expiresAt).toLocaleString()}. It is not sent by mail — copy it.`
            : undefined
        }
        onClose={() => setMagicLink(null)}
      />
    </div>
  );
}

/**
 * Creation is deliberately not tabbed: only the fields `createUser` accepts
 * are shown, and the rest of the record is filled in on the tabs afterwards.
 */
function UserCreateForm({
  form,
  scopeOptions,
  onCancel,
}: {
  form: UserForm;
  scopeOptions: { value: string; label: string }[];
  onCancel: () => void;
}) {
  const { values, set } = form;
  return (
    <FormLayout
      title="Account"
      onSubmit={form.submit}
      footer={
        <UserSaveFooter
          saving={form.saving}
          label="Create user"
          onCancel={onCancel}
        />
      }
    >
      <FormSection title="Identity">
        <FormField label="First name" required>
          <Input
            value={values.firstName}
            onChange={(event) => set('firstName', event.target.value)}
            required
          />
        </FormField>
        <FormField label="Last name">
          <Input
            value={values.lastName}
            onChange={(event) => set('lastName', event.target.value)}
          />
        </FormField>
        <FormField
          label="Email"
          required
          description="The only login identifier."
        >
          <Input
            type="email"
            value={values.email}
            onChange={(event) => set('email', event.target.value)}
            required
          />
        </FormField>
        <GuardSuggestion
          guard={form.guards.email}
          idPrefix="admin.users.email"
          disabled={form.saving}
          onAnswer={(address) => form.resubmit('email', address)}
        />
        <FormField
          label="Type"
          description={USER_TYPE_FIELD_DESCRIPTION}
        >
          <Select
            value={values.type}
            onChange={(event) => set('type', event.target.value)}
            options={USER_TYPE_OPTIONS}
          />
        </FormField>
      </FormSection>

      <FormSection
        title="Access"
        description="Scopes this user can reach. Assign ACL roles after creation."
      >
        <FormField label="Allowed scopes">
          <select
            multiple
            value={values.allowedScopeIds}
            onChange={(event) =>
              set(
                'allowedScopeIds',
                Array.from(
                  event.target.selectedOptions,
                  (option) => option.value,
                ),
              )
            }
            className="h-40 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          >
            {scopeOptions.map((scope) => (
              <option key={scope.value} value={scope.value}>
                {scope.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Default scope">
          <Select
            value={values.defaultScopeId}
            onChange={(event) => set('defaultScopeId', event.target.value)}
            options={[
              { value: '', label: 'No default' },
              ...scopeOptions.filter((scope) =>
                values.allowedScopeIds.includes(scope.value),
              ),
            ]}
          />
        </FormField>
        <FormField label="Default landing page">
          <Input
            value={values.defaultPage}
            onChange={(event) => set('defaultPage', event.target.value)}
            placeholder="/ or /orders"
          />
        </FormField>
      </FormSection>
    </FormLayout>
  );
}
